import classNames from 'classnames';
import Image from 'next/legacy/image';
import {useMemo} from 'react';

import {getResourcesURL} from '../../../urls';
import Row from '../../common/Row';
import {useBreakpoint} from '../../common/util/useBreakpoint';
import {ApiResourceResource} from '../../strapi/generated/contentTypes';
import {ProcessedValues} from '../../strapi/types';
import {TOC} from '../util/generateTOC';
import {getLatestResourcesUrl} from '../util/resourcesPageTracking';
import ResourceHeader from './ResourceHeader';
import RichText from './RichText';
import TableOfContents from './TableOfContents';

type ResourcePageContentParams = {
  resource: ProcessedValues<ApiResourceResource>;
  toc: TOC | undefined;
  draft?: boolean;
};

const ResourcePageContent = ({resource, toc, draft}: ResourcePageContentParams) => {
  const {isMobile} = useBreakpoint('mobile');
  const {isTablet} = useBreakpoint('tablet');
  const backUrl = getLatestResourcesUrl() || getResourcesURL();

  const hasTOC = toc && toc.length > 1;
  const sidebarWidth = hasTOC ? 'w-[400px]' : 'w-[150px]';

  const TOC = useMemo(
    () => (
      <div
        className={classNames(
          'flex-none self-start',
          sidebarWidth,
          'sticky top-[150px] max-h-[calc(100vh-150px)] mb-sm overflow-y-auto',
          'tablet:static tablet:max-h-fit tablet:mb-0 tablet:float-left',
          'mobile:w-auto mobile:float-none'
        )}
      >
        {hasTOC && (
          <TableOfContents toc={toc} className={'mx-sm mb-sm text-sm'} show={isMobile ? 6 : isTablet ? 12 : 20} />
        )}
      </div>
    ),
    [hasTOC, isMobile, isTablet, sidebarWidth, toc]
  );

  return (
    <>
      {/* Header row */}
      <ResourceHeader
        sideBarWidth={hasTOC ? '400' : '150'}
        backUrl={backUrl}
        label={resource.category ? resource.category.name + (draft ? ' (DRAFT)' : '') : undefined}
        title={resource.title}
        date={resource.date}
        author={resource.author?.name}
        extraBottomPadding
      />
      {/* Image row */}
      <Row className={'w-full justify-center'}>
        {!isTablet && <div className={classNames('flex-none', sidebarWidth)}>{/* spacer */}</div>}
        <div className={'w-full max-w-limit'}>
          <div className="w-full mb-24 px-sm max-w-limit -mt-28">
            <div className={'relative w-full h-[40rem]'}>
              {resource.image?.[0] && (
                <Image
                  className="rounded-3xl"
                  src={resource.image[0].src}
                  alt={resource.image[0].alt}
                  layout="fill"
                  objectFit="cover"
                />
              )}
            </div>
          </div>
        </div>
      </Row>
      {/* Content row */}
      <Row className={'w-full justify-center'}>
        {!isTablet && TOC}
        <div className={'w-full max-w-limit'}>
          {isTablet && TOC}
          <RichText
            className="max-w-limit px-sm mb-24 text-sm [&_h2]:font-semibold [&_h4]:text-base [&_img]:rounded-3xl"
            content={resource.html_body || resource.body}
          />
        </div>
      </Row>
    </>
  );
};

export default ResourcePageContent;
